import React from "react";
import CourieButton from "@src/common/components/Button";
import { Tooltip } from "@src/common/components";
import { ArrowsRightLeftIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";

type Props = {
  // Every checked stop, including those on days that are not shown.
  selectedCount: number;
  // The shown day only: what the dispatcher can count on screen.
  visibleSelectedCount: number;
  visibleTotalCount: number;
  onTransfer?: () => void;
};

/**
 * Toolbar above the route list. It is always rendered so that checking a stop
 * never shifts the list, and its label switches to a count once stops are
 * checked.
 */
export function RouteSelectionHeader({
  selectedCount,
  visibleSelectedCount,
  visibleTotalCount,
  onTransfer,
}: Props) {
  const hasSelection = selectedCount > 0;
  const hiddenSelectedCount = selectedCount - visibleSelectedCount;

  return (
    <div className="shrink-0 mb-1 flex h-8 items-center justify-between gap-2 border-b border-slate-200 dark:border-gray-700 pl-1 pr-0.5">
      <div className="flex items-center gap-1.5 min-w-0">
        <CheckCircleIcon
          className={classNames(
            "h-4 w-4 shrink-0",
            hasSelection
              ? "text-primary-500 dark:text-primary-400"
              : "text-gray-300 dark:text-gray-600"
          )}
        />
        <span
          className={classNames(
            "text-[11px] font-semibold uppercase tracking-wide truncate",
            hasSelection
              ? "text-primary-700 dark:text-primary-300"
              : "text-gray-400 dark:text-gray-500"
          )}
        >
          {hasSelection
            ? `${visibleSelectedCount} of ${visibleTotalCount} selected${
                hiddenSelectedCount > 0
                  ? ` (+${hiddenSelectedCount} on other days)`
                  : ""
              }`
            : "Select stops to transfer"}
        </span>
      </div>
      {onTransfer && (
        <Tooltip content="Transfer selected stops to another driver">
          <CourieButton
            size="xs"
            disabled={!hasSelection}
            onClick={onTransfer}
            aria-label="Transfer selected stops to another driver"
          >
            <ArrowsRightLeftIcon className="h-4 w-4" />
          </CourieButton>
        </Tooltip>
      )}
    </div>
  );
}

export default RouteSelectionHeader;
